import React, { useCallback, useState } from 'react'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { GameStatisticsUI } from './GameStatisticsUI'
import { useStore } from '@/models'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import type { PlayingStyle } from '@ppwr/poker'

const DEFAULT_GAME_STATS = {
  gameType: "Texas Hold'em",
  gameStartTime: Date.now(),
  percentage: { handsWon: 0, handsPlayed: 0, preFlopFold: 0, winsAtShowdown: 0 },
  playingStyle: 'Balanced Play' as PlayingStyle
}

export const GameStatisticsConvex = () => {
  const router = useRouter()
  const params = useLocalSearchParams<{ gameId?: string }>()
  const { user } = useStore().session
  const userId = user?.auth0Id || user?._id || ''

  const [leavingGame, setLeavingGame] = useState(false)

  // Get gameId from params or fetch last finished game
  const paramGameId = params.gameId as Id<'games'> | undefined

  // Fetch last finished game if no gameId provided
  const myFinishedGames = useQuery(api.metadata.queries.listMyGames, userId ? { userId, state: 'finished' } : 'skip')

  // Use param gameId or fallback to most recent finished game
  const gameId = paramGameId || myFinishedGames?.[0]?.gameId

  // Fetch stats from Convex
  const stats = useQuery(api.playerStats.queries.getPlayerGameStats, gameId && userId ? { gameId, userId } : 'skip')

  const gameDoc = useQuery(api.games.queries.getGame, gameId ? { gameId } : 'skip')
  const loungeId = gameDoc?.loungeId

  const isLoading = stats === undefined && gameId && userId
  const hasStats = Boolean(stats)

  const handleClose = useCallback(async () => {
    if (loungeId) {
      router.dismissTo(`/(root)/lounges/${loungeId}`)
      return
    }
    if (router.canGoBack()) {
      router.back()
      return
    }
    router.dismissTo('/(root)/(tabs)/play')
  }, [router])

  const goToPlayTab = useCallback(async () => {
    try {
      setLeavingGame(true)
      if (loungeId) {
        router.dismissTo(`/(root)/lounges/${loungeId}`)
      } else {
        router.dismissTo('/(root)/(tabs)/play')
      }
    } catch (e) {
      setLeavingGame(false)
      console.error('An error occurred', e)
    }
  }, [router, loungeId])

  const handlePressLeaderboardButton = useCallback(() => {
    router.navigate({
      pathname: '/(root)/game/leaderboard',
      params: gameId && userId ? { gameId, userId } : undefined
    })
  }, [router])

  // Show loading state while fetching

  // Protect against unexpected structure/nulls.
  const myGameStatsData = stats || DEFAULT_GAME_STATS

  if (isLoading || !hasStats) {
    return (
      <GameStatisticsUI
        loading={true}
        myGameStats={myGameStatsData}
        onClose={handleClose}
        onLeave={goToPlayTab}
        onLeaderboard={handlePressLeaderboardButton}
        fromLounge={!!loungeId}
      />
    )
  }

  // Extract userStats if present
  const userStats = stats && 'userStats' in stats ? stats.userStats : undefined

  return (
    <GameStatisticsUI
      loading={false}
      myGameStats={{
        gameType: myGameStatsData.gameType || "Texas Hold'em",
        gameStartTime: myGameStatsData.gameStartTime || Date.now(),
        percentage: myGameStatsData.percentage,
        playingStyle: (myGameStatsData.playingStyle || 'Balanced Play') as PlayingStyle
      }}
      userStats={userStats}
      onClose={handleClose}
      onLeave={goToPlayTab}
      onLeaderboard={handlePressLeaderboardButton}
      leavingGame={leavingGame}
      fromLounge={!!loungeId}
    />
  )
}
